import React from 'react';
import { GRANULARITY_LABELS } from '../../config/products';
import type { DataWindowState, DataWindowStatus } from '../../hooks/useDataWindow';
import { formatDayLabel, formatDayRange } from '../../utils/dateWindow';
import { stepDelivered } from '../../utils/deliveryBars';
import DeliveryDayBars from './DeliveryDayBars';
import DataStateNotice from './DataStateNotice';
import type { DataStateTone } from './DataStateNotice';

interface AvailableDatePickerProps {
  dw: DataWindowState;
}

type NotReady = Exclude<DataWindowStatus, 'ready' | 'loading'>;

const NOTICE: Readonly<Record<NotReady, { tone: DataStateTone; title: string }>> = {
  unsupported: { tone: 'info', title: 'Not available for this selection' },
  blocked: { tone: 'info', title: 'Not available yet' },
  'no-dates': { tone: 'info', title: 'No delivery days yet' },
  'no-access': { tone: 'warn', title: 'No data access' },
  unavailable: { tone: 'warn', title: 'Delivery days unavailable' },
  error: { tone: 'error', title: "Couldn't load delivery days" },
};

/**
 * Chooses the granularity and the delivery day from what was actually delivered. All state lives
 * in the data window; this component only shows it and reports clicks. Times are UTC.
 */
const AvailableDatePicker: React.FC<AvailableDatePickerProps> = ({ dw }) => {
  if (dw.status === 'loading') {
    return (
      <div role="status" className="w-full">
        <div className="h-8 w-full animate-pulse rounded bg-paper-200" aria-hidden="true" />
        <span className="sr-only">Loading delivery days</span>
      </div>
    );
  }

  if (dw.status !== 'ready') {
    const { tone, title } = NOTICE[dw.status];
    return (
      <DataStateNotice
        tone={tone}
        title={title}
        body={dw.reason ?? undefined}
        action={dw.retryable ? { label: 'Retry', onClick: dw.retry } : undefined}
      />
    );
  }

  const { selection } = dw;
  const heading = !dw.day
    ? 'No day chosen'
    : selection && selection.start !== selection.end
      ? formatDayRange(selection.start, selection.end)
      : formatDayLabel(dw.day);
  const prev = dw.day ? stepDelivered(dw.dates, dw.day, -1) : null;
  const next = dw.day ? stepDelivered(dw.dates, dw.day, 1) : null;

  return (
    <div className="w-full space-y-3">
      {dw.granularities.length >= 2 ? (
        <div className="join" role="group" aria-label="Time resolution">
          {dw.granularities.map((g) => {
            const selected = g === dw.granularity;
            return (
              <button
                key={g}
                type="button"
                className={`btn btn-sm join-item${selected ? ' btn-active' : ''}`}
                aria-pressed={selected}
                onClick={() => dw.setGranularity(g)}
              >
                {GRANULARITY_LABELS[g]}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-ink-muted">{GRANULARITY_LABELS[dw.granularity]} data only</p>
      )}

      <div>
        <div className="flex items-center justify-between gap-2">
          <p aria-live="polite" className="text-sm font-medium text-ink">
            {heading}
          </p>
          <div className="join">
            <button
              type="button"
              className="btn btn-ghost btn-xs join-item"
              aria-label="Previous delivery day"
              disabled={prev === null}
              onClick={() => prev && dw.setDay(prev)}
            >
              ‹
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-xs join-item"
              aria-label="Next delivery day"
              disabled={next === null}
              onClick={() => next && dw.setDay(next)}
            >
              ›
            </button>
          </div>
        </div>
        <DeliveryDayBars
          key={`${dw.granularity}|${dw.dates[0]}|${dw.latest}`}
          mode="select"
          label="Delivery day"
          dates={dw.dates}
          selection={dw.selection}
          rangeEnabled={dw.maxRangeDays > 1}
          onSelect={(d, { extend }) => (extend && dw.day ? dw.setRange(dw.day, d) : dw.setDay(d))}
          className="mt-2"
        />
        <p className="mt-1 text-xs text-ink-subtle">
          Times in {dw.zoneLabel}
          {dw.latest ? ` · latest ${formatDayLabel(dw.latest)}` : ''}
        </p>
        {dw.maxRangeDays > 1 ? (
          <p className="text-xs text-ink-subtle">
            Shift-click or press Shift+Enter to choose up to {dw.maxRangeDays} days.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default AvailableDatePicker;
